import type { ColDef } from 'ag-grid-community';
import { gridLocale } from '@h20-shared/grids/grid-context';
import type { GridColumnSettings } from '../grid-column.types';
import { MultiValueCellComponent } from './multi-value-cell.component';
import { formatMultiValue } from './multi-value.formatter';
import type { MultiValueCellParams, MultiValueColumnOptions } from './multi-value.types';

/** A column that shows several numbers in one cell, like `5 / 12`, and sorts by them in order. */
export function multiValueColumn<TRow>(
    options: MultiValueColumnOptions<TRow>
): GridColumnSettings<TRow> {
    const getCellNumbers = (row: TRow | undefined): readonly number[] | null => {
        if (row === undefined) {
            return null;
        }
        return options.getNumbers(row);
    };

    const settings: Omit<ColDef<TRow, readonly number[] | null>, 'field' | 'colId'> = {
        valueGetter: ({ data }) => getCellNumbers(data),
        // `valueGetter` returns a new array each time, so without this ag-grid redraws cells that did not change.
        equals: (left, right) => compareNumberLists(left, right) === 0,
        comparator: (left, right) => compareNumberLists(left, right),
        // A text filter searches the text the cell shows, so typing `5 | 12` finds `5 | 12 | 2`.
        filterValueGetter: ({ data, context }) => {
            const numbers = getCellNumbers(data);
            if (numbers === null || numbers.length === 0) {
                return null;
            }
            return formatMultiValue(numbers, gridLocale(context), options.separator);
        },
        cellRendererSelector: ({ data }) => {
            const { tooltip } = options;
            const params: MultiValueCellParams = {
                cellOptions: {
                    ...options,
                    getNumbers: () => getCellNumbers(data),
                    tooltip: tooltip && {
                        ...tooltip,
                        getInputs: () => (data === undefined ? null : tooltip.getInputs(data)),
                    },
                },
            };
            return { component: MultiValueCellComponent, params };
        },
    };
    return settings;
}

function compareNumberLists(
    left: readonly number[] | null | undefined,
    right: readonly number[] | null | undefined
): number {
    const leftNumbers = left ?? [];
    const rightNumbers = right ?? [];
    const shorterLength = Math.min(leftNumbers.length, rightNumbers.length);
    for (let index = 0; index < shorterLength; index++) {
        const difference = leftNumbers[index] - rightNumbers[index];
        if (difference !== 0) {
            return difference;
        }
    }
    return leftNumbers.length - rightNumbers.length;
}
